import type { Finding } from "../data/review-data"

/**
 * The page a finding sits on, or null when it belongs to no single page.
 *
 * A boxed finding carries its page inside the box; a page-level callout carries
 * `pageHint`; a document-level one has neither and is deliberately placed
 * nowhere rather than defaulted to page 1, which would put a mark on the ribbon
 * that nothing on that page explains.
 */
export function findingPage(finding: Finding): number | null {
  if (finding.bbox !== null) return finding.bbox.page
  return finding.pageHint
}

/**
 * Where a page in one document falls in another of a different length.
 *
 * The two documents are rarely the same length — a 70-page printed label
 * against a 113-page master — so holding the same page number drifts further
 * apart the deeper the reader goes, and pins both panes to the shorter one's
 * last page. Mapping by position keeps "roughly a third of the way in" meaning
 * the same thing on both sides, which is what a reviewer scrolling two
 * documents together is actually asking for.
 *
 * Equal lengths map one to one, so the common case is exact.
 */
export function mapPage(page: number, fromCount: number, toCount: number): number {
  if (fromCount <= 1 || toCount <= 1) return 1
  if (fromCount === toCount) return clampPage(page, toCount)
  const ratio = (page - 1) / (fromCount - 1)
  return clampPage(Math.round(ratio * (toCount - 1)) + 1, toCount)
}

export function clampPage(page: number, count: number): number {
  if (!Number.isFinite(page)) return 1
  return Math.min(Math.max(Math.round(page), 1), Math.max(1, count))
}
